"use client";
import { useCallback, useEffect, useMemo, useState } from "react";
import useGyroSensor from "../../customHooks/useGyroSensor";
import usePosthogPageview from "../../customHooks/usePosthogPageview";
import {
	checkCameraPermission,
	createObjectMetadataArray,
	generateUuid,
	getCurrentTimeInSeconds,
	getScanType,
	getVideoFPS,
	handleErrorMessage,
	handleScanTimeCapture,
	handleWebSocketCapture,
} from "../../utils/utils";
import LanguageContextProvider from "../../utils/context/languageContext";
import { ConfigProvider } from "../../utils/context/configContext";
import { getSwanService } from "../../utils/service/swanService";
import ParamsContext from "../../utils/context/paramsContext";
import MediaContextProvider from "../../utils/context/mediaContext";
import { BodyScanProps, UserDetails } from "../../types/interfaces";
import { BodyScanSteps } from "./BodyScanSteps";

const clothesFit = "0";

const BodyScan: React.FC<BodyScanProps> = (props) => {
	const {
		config,
		onRetry,
		onScanStart,
		onCaptureComplete,
		onUploadStart,
		onUploadEnd,
		onMeasurementSocketStart,
		onMeasurementSocketClose,
		onIntermediateScanSuccess,
		onScanError,
		onScanSuccess,
		onCustomScanSuccess,
		onComplete,
		isError,
		isSuccess,
	} = props;
	const userDetails: UserDetails = {
		email: "",
		shopDomain: "",
		gender: "male",
		heightInCm: 0,
		scanType: "",
		deviceFocalLength: 0,
		deviceModelName: "",
		sourceTag: "",
		callbackUrl: null,
		...(props.userDetails ?? {}),
	};
	const token = props.token ?? "";
	const { gender, scanType, shopDomain, heightInCm, email, deviceFocalLength, deviceModelName, callbackUrl, sourceTag } = userDetails;
	const swan = useMemo(() => getSwanService(token), [token]);
	const [uploadLoading, setUploadLoading] = useState(false);
	const [isVideoUploadedCorrect, setIsVideoUploadedCorrect] = useState(false);
	const [isMeasurementAvailable, setIsMeasurementAvailable] = useState(false);
	const [showDeniedModal, setShowDeniedModal] = useState({
		disabled: false,
		message: "",
	});
	const [scanFailsError, setScanFailsError] = useState("");
	const [isVideoUploaded, setIsVideoUploaded] = useState(false);
	const [loading, setLoading] = useState(true);
	const [startGyro, setStartGyro] = useState(false);
	const [scanUniqueKey, setScanUniqueKey] = useState("");
	const [scanStartTime, setScanStartTime] = useState<number | null>(getCurrentTimeInSeconds());
	const { gyroData } = useGyroSensor(startGyro);
	usePosthogPageview();

	const resetScan = useCallback(() => {
		setScanUniqueKey(generateUuid());
		setScanFailsError("");
		setUploadLoading(false);
		setIsVideoUploaded(false);
	}, []);

	const onError = (data: any) => {
		onScanError?.({ ...data, message: handleErrorMessage(data) });
		clearScanStartTimeAndScanId();
		setIsMeasurementAvailable(false);
		setScanFailsError(data);
		setIsVideoUploadedCorrect(false);
		handleScanTimeCapture({
			eventName: `${shopDomain}/measurement_failed/fit-view`,
			scanID: scanUniqueKey,
			status: "failed",
			email,
			message: handleErrorMessage(data),
		});
		if (scanStartTime)
			handleScanTimeCapture({
				eventName: `${shopDomain}/scan_completion_time`,
				scanID: scanUniqueKey,
				status: "failed",
				completionTime: getCurrentTimeInSeconds() - scanStartTime,
				email,
			});
	};

	const clearScanStartTimeAndScanId = () => {
		setScanStartTime(null);
		setScanUniqueKey("");
	};

	const onSuccess = useCallback(
		async (data: any) => {
			if (data && data?.scanStatus === "success" && data?.resultType === "intermediate" && data?.code === 200) {
				handleScanTimeCapture({
					eventName: `${shopDomain}/measurement_success/intermediate`,
					scanID: scanUniqueKey,
					status: "success",
					email,
				});
				setIsVideoUploadedCorrect(true);
				onIntermediateScanSuccess?.(data);
				return;
			}
			onScanSuccess?.(data);
			setIsVideoUploadedCorrect(true);
			clearScanStartTimeAndScanId();
			const scanIdToSet = scanUniqueKey;
			setIsMeasurementAvailable(true);
			handleScanTimeCapture({
				eventName: `${shopDomain}/measurement_success/fit-view`,
				scanID: scanIdToSet,
				status: "success",
				email,
			});
			if (scanStartTime)
				handleScanTimeCapture({
					eventName: `${shopDomain}/scan_completion_time`,
					scanID: scanIdToSet,
					status: "success",
					completionTime: getCurrentTimeInSeconds() - scanStartTime,
					email,
				});
		},
		[scanType, shopDomain, scanUniqueKey],
	);

	const handleMeasurementRecommendation = (scanId?: string) => {
		setIsMeasurementAvailable(false);
		setScanFailsError("");
		setIsVideoUploadedCorrect(false);
		swan.measurement.handleMeasurementSocket({
			scanId: scanId || scanUniqueKey,
			onPreopen: () => {
				handleWebSocketCapture({
					eventName: `${shopDomain}/webSocket`,
					scanID: scanUniqueKey,
					connection: "pre_open",
					type: "measurement_recommendation",
					email,
				});
			},
			onOpen: () => {
				onMeasurementSocketStart?.();
				handleWebSocketCapture({
					eventName: `${shopDomain}/webSocket`,
					scanID: scanUniqueKey,
					connection: "open",
					type: "measurement_recommendation",
					email,
				});
			},
			onClose: () => {
				onMeasurementSocketClose?.();
				handleWebSocketCapture({
					eventName: `${shopDomain}/webSocket`,
					scanID: scanUniqueKey,
					connection: "close",
					type: "measurement_recommendation",
					email,
				});
			},
			onError: (err) => {
				onError(err);
				handleWebSocketCapture({
					eventName: `${shopDomain}/webSocket`,
					scanID: scanUniqueKey,
					connection: "error",
					type: "measurement_recommendation",
					email,
				});
			},
			onSuccess: (data) => {
				handleWebSocketCapture({
					eventName: `${shopDomain}/webSocket`,
					scanID: scanUniqueKey,
					connection: "success",
					type: "measurement_recommendation",
					email,
				});
				onSuccess(data);
			},
		});
	};

	const uploadScanFile = useCallback(
		async (file: File, recordedFps?: number | null) => {
			const fileSizeInMB = Number((file.size / (1024 * 1024)).toFixed(2));
			// Use FPS captured at recording time, fallback to calculation if not available
			const videoFps = recordedFps !== undefined && recordedFps !== null ? recordedFps : await getVideoFPS(file);
			const arrayMetaData = createObjectMetadataArray({
				gender,
				focal_length: `${deviceFocalLength}`,
				height: `${heightInCm}`,
				customer_store_url: shopDomain,
				clothes_fit: clothesFit,
				scan_type: getScanType(scanType),
				callback_url: callbackUrl,
				source_tag: sourceTag,
			});
			handleScanTimeCapture({
				eventName: `${shopDomain}/body_scan_meta_data`,
				scanID: scanUniqueKey,
				email,
				data: JSON.stringify([...arrayMetaData, { fileSizeInMB, video_fps: videoFps || 0 }]),
			});
			onUploadStart?.();
			try {
				await swan.fileUpload.uploadFileFrontend({
					file,
					arrayMetaData,
					scanId: scanUniqueKey,
					email,
				});
				await swan.fileUpload.setDeviceInfo({
					model: deviceModelName,
					detection: "manual",
					gyro: gyroData,
					scanId: scanUniqueKey,
				});
				console.log("video successfully uploaded");
				setIsVideoUploaded(false);
				handleScanTimeCapture({
					eventName: `${shopDomain}/scan_success`,
					scanID: scanUniqueKey,
					status: "success",
					email,
					data: JSON.stringify(arrayMetaData),
				});
				handleScanTimeCapture({
					eventName: "scan finished",
					scanID: scanUniqueKey,
					status: "success",
					email,
				});
				setScanStartTime(getCurrentTimeInSeconds());
				setTimeout(() => {
					setIsVideoUploaded(true);
				}, 3000);
			} catch (error:any) {
				handleScanTimeCapture({
					eventName: "scan finished",
					scanID: scanUniqueKey,
					status: "failed",
					email,
					message: handleErrorMessage(error),
				});
				handleScanTimeCapture({
					eventName: `${shopDomain}/scan_failed`,
					scanID: scanUniqueKey,
					status: "failed",
					email,
					message: handleErrorMessage(error),
					data: JSON.stringify(arrayMetaData),
				});
				setScanFailsError(handleErrorMessage(error));
				setIsVideoUploaded(false);
				onScanError?.({...error, message: handleErrorMessage(error) });
				clearScanStartTimeAndScanId();
			} finally {
				setStartGyro(false);
				onUploadEnd?.();
			}
		},
		[scanUniqueKey, gyroData, onUploadStart, onUploadEnd],
	);
	// const checkMeasurementStatus = useCallback(
	// 	async (scanId: string) => {
	// 		try {
	// 			const res = await swan.measurement.getMeasurementResult(scanId);
	// 			const isMeasured = res?.data?.isMeasured;
	// 			const tempScanStartTime = scanStartTime || getCurrentTimeInSeconds();
	// 			const currentTimeInSeconds = getCurrentTimeInSeconds();
	// 			const fiveMinutesInSeconds = 5 * 60;
	// 			setLoading(false);
	// 			if (isMeasured === false) {
	// 				clearScanStartTimeAndScanId();
	// 				setScanUniqueKey(generateUuid());
	// 				return;
	// 			}
	// 			setUploadLoading(true);
	// 			if (isMeasured === true) {
	// 				await onSuccess(null);
	// 			} else if (isMeasured === null && tempScanStartTime > currentTimeInSeconds + fiveMinutesInSeconds) {
	// 				onError(res?.data?.error);
	// 			} else {
	// 				handleMeasurementRecommendation(scanId);
	// 			}
	// 		} catch (error) {
	// 			console.log(error);
	// 			clearScanStartTimeAndScanId();
	// 			setScanUniqueKey(generateUuid());
	// 			setUploadLoading(false);
	// 		}
	// 	},
	// 	[onError, onSuccess, setScanUniqueKey],
	// );

	const handleCameraPermission = useCallback(async () => {
		if (+heightInCm < 152.4 || +heightInCm > 213.36) {
			onScanError?.({
				message: "Height must be between 152.4cm (5ft) and 213.36cm (7ft)",
			});
			return;
		}

		const permission = await checkCameraPermission();
		if (permission.disabled) {
			handleScanTimeCapture({
				eventName: `${shopDomain}/camera_activation`,
				scanID: scanUniqueKey,
				status: "failed",
				email,
			});
			setLoading(false);
		} else {
			// const scanId = scanUniqueKey;
			// if (scanId) {
			// 	checkMeasurementStatus(scanId);
			// } else {
			// 	setLoading(false);
			// }
			setLoading(false);
			handleScanTimeCapture({
				eventName: `${shopDomain}/camera_activation`,
				scanID: scanUniqueKey,
				status: "success",
				email,
			});
		}
		setShowDeniedModal(permission);
		setScanFailsError("");
		setIsVideoUploaded(false);
	}, [email, scanUniqueKey, setScanUniqueKey, shopDomain]);

	useEffect(() => {
		if (isError || isSuccess) return;
		if (shopDomain) {
			handleCameraPermission();
		}
	}, [shopDomain, heightInCm, isError, isSuccess]);

	useEffect(() => {
		if (isError || isSuccess) return;
		if (isVideoUploaded && shopDomain && scanUniqueKey) {
			handleMeasurementRecommendation();
		}
	}, [isVideoUploaded, shopDomain, scanUniqueKey, isError, isSuccess]);
	return (
		<div data-testid="body-scan-root">
		<LanguageContextProvider>
			<ConfigProvider config={config}>
				<MediaContextProvider>
					<ParamsContext.Provider
						value={{
							userDetails,
							onRetry,
							onScanError,
							isError,
							isSuccess,
							onScanSuccess,
							gender,
							scanUniqueKey,
							scanFailsError,
							setScanUniqueKey,
							setIsVideoUploaded,
							isMeasurementAvailable,
							isVideoUploadedCorrect,
							loading,
							showDeniedModal,
							uploadLoading,
							setStartGyro,
							uploadScanFile,
							setUploadLoading,
							resetScan,
							setScanFailsError,
							setScanStartTime,
							onScanStart,
							onCaptureComplete,
							onCustomScanSuccess,
							onComplete,
							swan,
						}}
					>
						<BodyScanSteps />
					</ParamsContext.Provider>
				</MediaContextProvider>
			</ConfigProvider>
		</LanguageContextProvider>
		</div>
	);
};

// SSR mount-gate: the camera/scan tree is browser-only (touches window/document
// during render and uses the webcam). This thin wrapper renders a neutral
// placeholder on the server and the first client render, then mounts the real
// component only after the effect fires in the browser. This lets consumers
// import BodyScan into a server-rendered app (Next/Remix) without a crash or a
// dynamic(ssr:false) wrapper. None of BodyScanInner's hooks run on the server.
const BodyScanClientGate: React.FC<BodyScanProps> = (props) => {
	const [mounted, setMounted] = useState(false);
	useEffect(() => {
		setMounted(true);
	}, []);

	if (!mounted) {
		return <div data-testid="body-scan-root" />;
	}

	return <BodyScan {...props} />;
};

export { BodyScanClientGate as BodyScan };
